import type { ReactNode } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { type LucideIcon } from 'lucide-react'
import { Trash2Icon } from 'lucide-react'

import { tv } from 'tailwind-variants'

import type { ModalComponentProps } from './modal.store'

import type { VariantProps } from 'tailwind-variants'

export type AlertDialogProps = {
  title: string
  /** Plain text (`\n` breaks lines) or custom content. */
  description: ReactNode
  confirmLabel: string
  icon?: LucideIcon
  variant?: Exclude<VariantProps<typeof modalVariants>['variant'], undefined>
}

const modalVariants = tv({
  slots: {
    base: '',
    media: '',
  },
  variants: {
    variant: {
      default: {},
      destructive: {
        media: 'bg-destructive/20 text-destructive',
      },
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

export function AlertDialogModal({
  props,
  onResolve,
}: ModalComponentProps<'ALERT_DIALOG'>) {
  // Default to trash for 'destructive', always allow overrides.
  const Icon =
    !props.icon && props.variant === 'destructive' ? Trash2Icon : props.icon

  return (
    <AlertDialog
      open={true}
      onOpenChange={() => {
        onResolve(false) // Cancelled
      }}
    >
      <AlertDialogContent size="sm">
        <AlertDialogHeader>
          {Icon && (
            <AlertDialogMedia
              className={modalVariants().media({ variant: props.variant })}
            >
              <Icon />
            </AlertDialogMedia>
          )}

          <AlertDialogTitle>{props.title}</AlertDialogTitle>
          <AlertDialogDescription
            // A div, so custom content can hold block elements
            render={<div />}
            className="whitespace-pre-line"
          >
            {props.description}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel variant="outline">Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant={
              props.variant === 'destructive' ? 'destructive' : 'default'
            }
            onClick={() => {
              onResolve(true) // Confirmed
            }}
          >
            {props.confirmLabel ?? 'Confirm'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
